import { Children, cloneElement, isValidElement, type ReactNode } from 'react';
import type { PortableTextBlock } from '@portabletext/types';
import type { TextClassName } from '../marks/className.mark';
import { isMarkEl } from './isMarkEl';

// Classes that set font-size and therefore need their paired line-height on the
// block element — an inline <span> keeps the block strut's line-height, which
// mismatches the smaller font-size. Mirrors the size groups in the Studio class
// list (studio/schemaTypes/objects/richText.ts). Color classes stay inline.
const SIZE_CLASSES = new Set<TextClassName>([
    'display-1',
    'display-2',
    'display-3',
    'display-4',
    'heading-1',
    'heading-2',
    'heading-3',
    'heading-4',
    'heading-5',
    'heading-6',
    'text-xl',
    'text-lg',
    'text-md',
    'text-sm',
]);

type ClassNameMarkDef = { _key: string; _type: 'className'; value?: TextClassName | TextClassName[] };
type SpanChild = { _type: 'span'; _key?: string; marks?: string[] };

function toClassArray(value?: TextClassName | TextClassName[]): string[] {
    return Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];
}

// Find className annotations covering the whole block (every text span carries the
// mark). Their classes belong on the block tag, not on an inline span. Returns the
// collected classes plus the markDef keys whose <span> wrappers must be dropped.
export function getLiftedClasses(value: PortableTextBlock): { classes: string[]; keys: Set<string> } {
    const keys = new Set<string>();
    const classes: string[] = [];

    const spans = ((value?.children ?? []) as { _type?: string }[]).filter((c): c is SpanChild => c?._type === 'span');
    if (spans.length === 0) return { classes, keys };

    const defs = ((value?.markDefs ?? []) as { _type?: string; _key?: string }[]).filter(
        (d): d is ClassNameMarkDef => d?._type === 'className' && typeof d._key === 'string',
    );
    for (const def of defs) {
        const coversAll = spans.every((s) => Array.isArray(s.marks) && s.marks.includes(def._key));
        if (!coversAll) continue;
        const defClasses = toClassArray(def.value);
        if (defClasses.length === 0) continue;
        keys.add(def._key);
        classes.push(...defClasses);
    }
    return { classes, keys };
}

// Merge the block's base class with lifted classes: a lifted size class REPLACES the
// base size (so font-size + line-height stay paired), other lifted classes (colors)
// are appended.
export function mergeBlockClasses(baseClass: string, lifted: string[]): string {
    if (lifted.length === 0) return baseClass;
    const sizes = lifted.filter((c) => SIZE_CLASSES.has(c as TextClassName));
    const rest = lifted.filter((c) => !SIZE_CLASSES.has(c as TextClassName));
    const base = sizes.length > 0 ? sizes : baseClass ? [baseClass] : [];
    return Array.from(new Set([...base, ...rest])).join(' ');
}

// Drop the wrapping <span> for className marks whose classes were lifted onto the
// block tag, keeping their content. Recurses because a lifted mark may sit inside
// another block-covering mark (e.g. align).
export function unwrapLiftedClassNames(children: ReactNode, keys: Set<string>): ReactNode {
    if (keys.size === 0) return children;
    return Children.map(children, (node) => {
        if (isMarkEl(node, 'className')) {
            const markKey = (node.props as { markKey?: string }).markKey;
            if (markKey && keys.has(markKey)) {
                return unwrapLiftedClassNames((node.props as { children?: ReactNode }).children, keys);
            }
        }
        if (isValidElement(node)) {
            const props = node.props as { children?: ReactNode };
            if (props.children != null) {
                return cloneElement(node, undefined, unwrapLiftedClassNames(props.children, keys));
            }
        }
        return node;
    });
}
